import { FC, useState } from 'react';
import { Button, Divider, Icon, Tooltip } from 'common-components';
import styles from './credentialsCreationCard.module.scss';
import { CredentialsCreationCardProps, DateTypes, CreationData } from './credentialsCreationCardTypes';
import { useTranslationContext } from '../../contexts/translationContext';
import DropDown from '../dropDown/dropDown';

const CredentialsCreationCard: FC<CredentialsCreationCardProps> = (props: CredentialsCreationCardProps) => {
    const {
        createHandler,
        isEdit = false,
        credentialName,
        expirationDateType,
        expirationAmount,
        onBack,
        isDisabled = false
    } = props;

    const [isEditing, setIsEditing] = useState<boolean>(isEdit);
    const [amount, setAmount] = useState<number>(expirationAmount || 30);
    const [dateType, setDateType] = useState<string>(expirationDateType || Object.keys(DateTypes)[0]);
    const [credName, setCredName] = useState<string>(credentialName || '');

    const { translate } = useTranslationContext();

    const CREATE_NEW_CREDENTIALS = translate('CreateNewCredentials');
    const CREATE_NEW = translate('CreateNew');
    const BACK = translate('Back');
    const VALID_FOR = translate('ValidFor');
    const TYPE_CREDENTIAL_NAME_PLACEHOLDER = translate('TypeCredentialNamePlaceholder');
    const EDIT_CREDENTIALS = translate('EditCredentials');
    const SAVE_CHANGES = translate('SaveChanges');
    const DISABLE_CREATION_TOOLTIP = translate('CredentialCreationAmountExceeded');

    const canCreate = () => {
        return credName.length > 0;
    };

    const onCreateClick = () => {
        const data: CreationData = {
            amount,
            dateType: DateTypes[dateType].toString(),
            name: credName
        };
        createHandler(data);
        setIsEditing(false);
    };

    const dateTypeOptions = () => {
        return Object.keys(DateTypes).map(key => {
            return { key: translate(key), value: translate(key) };
        });
    };

    const amountOptions = () => {
        return Array.from({ length: 30 }, (_, i) => i + 1).map(key => {
            return { key: key.toString(), value: translate(key) };
        });
    };

    const expiration = () => {
        return (
            <>
                <DropDown
                    title={translate(dateType)}
                    options={dateTypeOptions()}
                    onSelect={option => setDateType(option.value)}
                    optionsStyle={{ maxHeight: '7rem', overflow: 'scroll' }}
                    optionStyle={{ fontWeight: '200' }}
                />
                <DropDown
                    title={amount.toString()}
                    options={amountOptions()}
                    isOptionCentered={true}
                    onSelect={option => setAmount(parseInt(option.key, 10))}
                    optionsStyle={{ maxHeight: '7rem', overflow: 'scroll' }}
                    optionStyle={{ fontWeight: '200' }}
                />
            </>
        );
    };

    const editView = () => {
        return (
            <div className={styles['edit-container']}>
                <div className={styles.title}>
                    <span>{isEdit ? EDIT_CREDENTIALS : CREATE_NEW_CREDENTIALS}</span>
                </div>
                <div className={styles.content}>
                    <div className={styles['input-wrapper']}>
                        <input
                            placeholder={TYPE_CREDENTIAL_NAME_PLACEHOLDER}
                            onChange={e => setCredName(e.target.value)}
                            defaultValue={credentialName}
                        />
                    </div>
                    <div className={styles['expiration-wrapper']}>
                        <span>{VALID_FOR}</span>
                        {expiration()}
                    </div>
                </div>
                <div className={styles['divider-container']}>
                    <Divider orientation="horizontal" color="rgba(98, 98, 98, 0.30)" />
                </div>
                <div className={styles.actions}>
                    <Button
                        type={'learningPanelAdminNoBg'}
                        startIcon={<Icon name={'adminArrowLeft'} />}
                        text={BACK}
                        onClick={() => {
                            setIsEditing(false);
                            onBack?.();
                        }}
                        lowerCase={true}
                        className={styles['bottun-cx']}
                    />
                    <Button
                        type={canCreate() ? 'gradient' : 'gradientDisableSameColor'}
                        text={isEdit ? SAVE_CHANGES : CREATE_NEW}
                        onClick={() => onCreateClick()}
                        lowerCase={true}
                        className={styles['bottun-cx']}
                    />
                </div>
            </div>
        );
    };

    const cardView = () => {
        return (
            <div className={styles['card-container']}>
                <div className={styles.title}>
                    <span>{CREATE_NEW_CREDENTIALS}</span>
                </div>
                <div className={styles['image-container']}>
                    <Icon name={'keys'} className={styles.icon} isPng={true} />
                </div>
                <Tooltip title={isDisabled ? DISABLE_CREATION_TOOLTIP : ''} placement="top-start">
                    <div>
                        <Button
                            type={isDisabled ? 'gradientDisableSameColor' : 'gradient'}
                            text={CREATE_NEW}
                            onClick={() => setIsEditing(true)}
                            lowerCase={true}
                            style={{ padding: '0.5rem 3rem' }}
                        />
                    </div>
                </Tooltip>
            </div>
        );
    };

    return <div className={styles.container}>{isEditing ? editView() : cardView()}</div>;
};

export default CredentialsCreationCard;
